import { createORPCClient } from "@-/com.npmjs.orpc__client";
import { RPCLink } from "@-/com.npmjs.orpc__client/fetch";
import type { RouterClient } from "@-/com.npmjs.orpc__server";
import type { OrpcRouter } from "@-/dev.improve.haniverse.api";
import {
  getAuthCookieHeader,
  getBetterAuthBaseUrl,
} from "@-/dev.improve.haniverse.lib.auth.client";
import { parseCookie, stringifyCookie } from "@-/com.npmjs.cookie-es";
import { isFullString } from "@-/com.npmjs.is-what";
import { fetchOrpcWithDeadline } from "#fetch-orpc-with-deadline";
import { createOrpcLinkPlugins } from "#orpc-link-plugins";

function lastNonEmptyCookieValue(value: string | string[]): string | undefined {
  if (Array.isArray(value)) {
    const index = value.findLastIndex((item) => isFullString(item));
    return index === -1 ? undefined : value[index];
  }
  return isFullString(value) ? value : undefined;
}

function resolveBaseUrl(): string {
  // Both better-auth and oRPC live on the standalone backend (apps/backend). Reuse the
  // same URL so the auth cookie is sent on the same origin as RPC calls.
  return getBetterAuthBaseUrl();
}

/**
 * De-poison a Cookie header string.
 *
 * Better Auth's sign-up/sign-in response sets TWO `better-auth.session_data`
 * cookies: an empty one (`session_data=`, the `Max-Age=0` clear directive) and a
 * populated one (`session_data=eyJ…`, the encrypted session cache). When
 * `@better-auth/expo`'s `getCookie()` serializes its stored jar, both can end up
 * in the string as `session_data=; session_data=eyJ…`. The empty value, parsed
 * first server-side, blanks the cookie cache and makes `getSession()` return null
 * — so every authenticated oRPC call (e.g. `database.actor.ensure`) fails with
 * "Not signed in" even though the user is signed in.
 *
 * Collapse duplicates to the LAST non-empty value per cookie name, and drop
 * name-only/empty pairs entirely. The token cookie alone isn't enough (the
 * server reads the encrypted `session_data` cache), so keeping the populated one
 * is what restores auth.
 */
function dePoisonCookieHeader(cookieHeader: string): string {
  const cookies = parseCookie(cookieHeader, { allowMultiple: true });
  const dePoisoned: Record<string, string> = {};
  for (const [name, value] of Object.entries(cookies)) {
    if (value === undefined) {
      continue;
    }
    const resolved = lastNonEmptyCookieValue(value);
    if (resolved !== undefined) {
      dePoisoned[name] = resolved;
    }
  }
  return stringifyCookie(dePoisoned);
}

/**
 * The session cookie for an authenticated oRPC call. Goes through the typed, awaited
 * `getAuthCookieHeader` on purpose: two earlier untyped reads here (an `in` guard the auth
 * client's Proxy never satisfies, then a `Reflect.get` + `typeof === "string"` check that a
 * `Promise` never passes) each kept compiling while silently sending NO cookie, so every
 * authenticated call failed "Not signed in" and every sign-in left the device local-only.
 */
async function resolveCookieHeader(): Promise<string | undefined> {
  const cookie = await getAuthCookieHeader();
  if (cookie.length === 0) {
    return undefined;
  }
  const dePoisoned = dePoisonCookieHeader(cookie);
  return dePoisoned.length > 0 ? dePoisoned : undefined;
}

const link = new RPCLink({
  // Bounded per procedure, headers and body; see fetch-orpc-with-deadline.ts. Without it a backend
  // that accepted the connection and never answered left the calling button or screen spinning
  // forever (React Native's fetch has no timeout, and no call site passes a signal).
  fetch: async (request, init, _options, path) => fetchOrpcWithDeadline(request, init, path),
  headers: async () => {
    const cookie = await resolveCookieHeader();
    return cookie != null ? { cookie } : {};
  },
  plugins: createOrpcLinkPlugins(),
  // oRPC's `url` is the full base URL (it has no separate `origin` option);
  // resolve the backend origin per-request and append the RPC path.
  url: () => `${resolveBaseUrl()}/api/rpc`,
});

export const getOrpcClient: RouterClient<OrpcRouter> = createORPCClient(link);
